import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { NudgeQueueSummary } from '../../../types/api'
import { relativeTimeLabel } from '../utils'
import { useDashboard } from '../DashboardContext'
import { UpgradePrompt } from './UpgradePrompt'

const HOURS_48 = 48 * 60 * 60 * 1000

function isRecent(ts: string): boolean {
  return Date.now() - new Date(ts).getTime() < HOURS_48
}

type Props = {
  queueRows: NudgeQueueSummary[]
}

export function RecoveryAlertBanner({ queueRows }: Props) {
  const { isFeatureAllowed } = useDashboard()
  const [dismissed, setDismissed] = React.useState<Set<string>>(new Set())

  const recentRecoveries = React.useMemo(
    () => queueRows.filter(r => r.status === 'RECOVERED' && isRecent(r.updatedAt)),
    [queueRows],
  )

  const visible = recentRecoveries.filter(r => !dismissed.has(r.id))

  if (!isFeatureAllowed('RECOVERY_ALERTS')) {
    return (
      <UpgradePrompt
        title="Recovery alerts"
        blurb="Get notified the moment a lapsed customer responds. Recovery alerts are available on paid plans."
      />
    )
  }

  if (visible.length === 0) return null

  return (
    <div className="recovery-banners">
      <AnimatePresence initial={false}>
        {visible.map(row => (
          <motion.div
            key={row.id}
            className="recovery-banner"
            initial={{ opacity: 0, y: -12, scaleY: 0.95 }}
            animate={{ opacity: 1, y: 0, scaleY: 1 }}
            exit={{ opacity: 0, y: -8, scaleY: 0.95 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >
            <span className="recovery-banner-icon" aria-hidden="true">✦</span>
            <span className="recovery-banner-text">
              <strong>{row.contactName}</strong> has confirmed they're coming back!
              <span className="recovery-banner-time"> · recovered {relativeTimeLabel(row.updatedAt)}</span>
            </span>
            <button
              className="recovery-banner-close"
              aria-label={`Dismiss recovery alert for ${row.contactName}`}
              onClick={() => setDismissed(prev => new Set([...prev, row.id]))}
            >
              ×
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
